/**
 * The daily alignment axes (blueprint SC-27 "Daily Energy", §14).
 *
 * Four user-facing categories, one order, one set of band words, one mean.
 * Shared, so the API's prose and the coach app's meters cannot drift apart.
 * Every number here is an app-generated reflection index, never a probability,
 * which is why the bands are words about the day and not about an outcome.
 */

export const ALIGNMENT_AXES = [
  { key: 'business', label: 'Business' },
  { key: 'money', label: 'Money' },
  { key: 'focus', label: 'Focus' },
  { key: 'relationships', label: 'Relationships' },
] as const;

export type AlignmentAxis = (typeof ALIGNMENT_AXES)[number]['key'];

/** The four axes plus their rounded mean. */
export type AlignmentSnapshot = Record<AlignmentAxis, number> & { overall: number };

export const ALIGNMENT_BANDS = ['Strong', 'Supportive', 'Balanced', 'Gentle'] as const;
export type AlignmentBand = (typeof ALIGNMENT_BANDS)[number];

/**
 * The word beside a score. 78 and 65 are the Align hub's own day-sentence cut
 * points, so the overall band and the sentence under it can never disagree;
 * the blueprint's sample lines (88 Strong, 82 Strong, 67 Balanced) then leave
 * 70 as the Supportive floor. Its one remaining line, 84 Supportive, is not
 * monotonic with the 82 above it and is deliberately overridden: 84 is Strong.
 */
export function alignmentBand(score: number): AlignmentBand {
  if (score >= 78) return 'Strong';
  if (score >= 70) return 'Supportive';
  if (score >= 65) return 'Balanced';
  return 'Gentle';
}

/** The day's overall index: the rounded mean of the four axes. */
export function alignmentOverall(axes: Record<AlignmentAxis, number>): number {
  const total = ALIGNMENT_AXES.reduce((sum, { key }) => sum + axes[key], 0);
  return Math.round(total / ALIGNMENT_AXES.length);
}
